import { useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Tabs from "@mui/material/Tabs";
import { getClientById } from "../../../services/api/client";
import { fetchUserDetails } from "../../../services/api/user";
import CustomerAccountUsersTable from "../CustomerAccountUsersTable";
import CustomerEntitiesTable from "../CustomerEntitiesTable";
import BaseWrapper from "../../../components/ui/BaseWrapper";
import ProfileDetailsHeader from "../../ProfileDetails/ProfileDetailsHeader";
import CustomerProfile from "../../ProfileDetails/CustomerProfile";
import CustomerDetailsTab from "./CustomerDetailsTab";
import { styled } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import { brandFontFamily, brandInputTextColor, brandPrimaryColor } from "../../../utils/variables";

const CustomTab = styled((props) => <Tab disableRipple {...props} />)(({ theme }) => ({
  textTransform: "none",
  minWidth: 0,
  [theme.breakpoints.up("sm")]: {
    minWidth: 0,
  },
  height: "3px",
  width: "230px",
  fontWeight: 300,
  fontSize: "16px",
  boxShadow: "0px -1px 8px 0px rgba(0, 0, 0, 0.09)",
  borderRadius: "6px",
  margin: "13px 0",
  background: "white",
  marginRight: theme.spacing(1),
  color: brandInputTextColor,
  fontFamily: brandFontFamily,
  "&.Mui-selected": {
    color: brandPrimaryColor,
    fontWeight: 400,
    borderBottom: `1px solid ${brandPrimaryColor}`,
  },
}));

const TABS = [
  { key: "profile", label: "Profile" },
  { key: "details", label: "Company Details" },
  { key: "entities", label: "Entities" },
  { key: "users", label: "Users" },
];

const CustomerDetails = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const requestedIndex = TABS.findIndex((tab) => tab.key === searchParams.get("tab"));
  const activeIndex =
    requestedIndex === -1 ? TABS.findIndex((tab) => tab.key === "details") : requestedIndex;
  const activeTab = TABS[activeIndex].key;

  const { data: customerData } = useQuery({
    queryKey: ["client", id],
    queryFn: () => getClientById(id),
    enabled: !!id,
  });
  const customer = customerData?.data;
  const ownerId = customer?.ownerId;

  const { data: owner, refetch: refetchOwner } = useQuery({
    queryKey: ["userData", String(ownerId), false],
    queryFn: () => fetchUserDetails(ownerId),
    enabled: !!ownerId,
  });

  const handleChange = (event, newValue) => {
    const params = new URLSearchParams(searchParams);
    params.set("tab", TABS[newValue].key);
    setSearchParams(params, { replace: true });
  };

  return (
    <div className="bg-light min-h-full">
      <div className="border-b border-zinc-300 bg-white py-6">
        <BaseWrapper>
          {owner ? (
            <ProfileDetailsHeader user={owner} />
          ) : (
            <h3 className="md:text-left text-center">{customer?.clientName || "Customer"}</h3>
          )}
        </BaseWrapper>
      </div>

      <BaseWrapper>
        <Tabs
          TabIndicatorProps={{ style: { display: "none" } }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          value={activeIndex}
          onChange={handleChange}
          aria-label="customer tabs"
          sx={{
            "& .MuiTabs-flexContainer": { overflow: "visible !important" },
            "& .MuiTabs-scrollButtons.Mui-disabled": { width: 0, opacity: 0 },
          }}
        >
          {TABS.map((tab) => (
            <CustomTab
              key={tab.key}
              label={tab.label}
              sx={{ minHeight: "38px", marginRight: "40px" }}
            />
          ))}
        </Tabs>
      </BaseWrapper>

      {activeTab === "profile" && (
        <BaseWrapper className="pb-6">
          {owner && <CustomerProfile user={owner} refetchMe={refetchOwner} hideCustomerInfo />}
        </BaseWrapper>
      )}

      {activeTab === "details" && (
        <BaseWrapper className="pb-6">
          <CustomerDetailsTab customer={customer} />
        </BaseWrapper>
      )}

      {activeTab === "users" && <CustomerAccountUsersTable hideTitle />}

      {activeTab === "entities" && (
        <BaseWrapper>
          <CustomerEntitiesTable />
        </BaseWrapper>
      )}
    </div>
  );
};

export default CustomerDetails;
